import {Injectable} from '@angular/core';
import {LocalStorageService} from "../../../../../base/services/storage/localstorage.service";
import {environment} from "../../../../../../environments/environment";
import {EventBusService} from "../../../../../base/services/eventbus/event-bus.service";
import {jwtDecode} from "jwt-decode";
import {IAuth, TokenPayload} from "../models/auth.model";
import Keycloak, {KeycloakInitOptions} from "keycloak-js";
import {catchError, from, Observable, of, throwError} from "rxjs";

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private keycloak: Keycloak | null = null;
  private initPromise: Promise<boolean> | null = null;

  constructor(
    private eventBus: EventBusService,
  ) {
  }

  initialize(): Promise<boolean> {
    if (this.initPromise) {
      return this.initPromise;
    }

    const config = this.resolveKeycloakConfig();
    this.keycloak = new Keycloak(config);

    const initOptions: KeycloakInitOptions = {
      onLoad: 'check-sso',
      checkLoginIframe: false,
      pkceMethod: 'S256',
      responseMode: 'query',
      redirectUri: window.location.href,
    };

    const initPromise = this.keycloak.init(initOptions)
      .then((authenticated: boolean) => authenticated)
      .catch((error: unknown) => {
        this.initPromise = null;
        throw error;
      });

    this.initPromise = initPromise;
    return initPromise;
  }

  initializeForStartup(): Promise<boolean> {
    if (!this.shouldInitializeOnStartup(window.location)) {
      return Promise.resolve(false);
    }
    return this.initialize();
  }

  async login(returnPath = window.location.pathname + window.location.search + window.location.hash): Promise<void> {
    await this.initialize();
    const keycloak = this.requireKeycloak();
    if (keycloak.authenticated) {
      return;
    }

    await keycloak.login({
      redirectUri: this.toAbsoluteUrl(returnPath),
      scope: this.getLoginScopes()
    });
  }

  refreshToken(key: string): Observable<IAuth> {
    return from(this.refreshTokenAsync()).pipe(
      catchError(error => {
        if (error.status >= 500 || error.status === 0) {
          this.eventBus.publish(`${key}:error:1`, error);
        } else {
          this.eventBus.publish(`${key}:error:2`, error);
        }
        return throwError(() => error);
      })
    );
  }

  ensureFreshToken(minValiditySeconds = 30): Observable<string> {
    return from(this.ensureFreshTokenAsync(minValiditySeconds));
  }

  private async refreshTokenAsync(minValiditySeconds = 30): Promise<IAuth> {
    await this.initialize();
    const keycloak = this.requireKeycloak();
    if (!keycloak.authenticated) {
      throw new Error('Cannot refresh token while unauthenticated');
    }
    await keycloak.updateToken(minValiditySeconds);
    return this.buildAuthSnapshot();
  }

  private async ensureFreshTokenAsync(minValiditySeconds = 30): Promise<string> {
    const auth = await this.refreshTokenAsync(minValiditySeconds);
    return auth.access_token;
  }

  isAuthenticatedAsync(): Observable<boolean> {
    return of(this.isAuthenticated());
  }

  isAuthenticated() {
    return !!this.keycloak?.authenticated;
  }

  isAdmin(): boolean {
    return this.isAuthenticated()
      && this.hasPrivilege("ADMINISTRATION")
  }

  isAdminAsync(): Observable<boolean> {
    return of(this.isAuthenticated()
      && this.hasPrivilege("ADMINISTRATION")
    )
  }

  getPayload(): TokenPayload {
    const parsed = this.keycloak?.tokenParsed as TokenPayload | undefined;
    if (parsed) {
      return parsed;
    }
    return jwtDecode<TokenPayload>(this.getAccessToken());
  }

  getAuthorities(): string[] {
    const decoded = this.getPayload()
    return decoded.resource_access?.[environment.auth_client_id]?.roles ?? []
  }

  getAuthoritiesAsync(): Observable<string[]> {
    const decoded = this.getPayload()
    return of(decoded.resource_access?.[environment.auth_client_id]?.roles ?? [])
  }

  hasPrivilege(privilege: string): boolean {
    const authorities = this.getAuthorities()
    if (!authorities) return false
    return authorities.includes(privilege)
  }

  hasPrivilegeAsync(privilege: string): Observable<boolean> {
    const authorities = this.getAuthorities()
    if (!authorities) return of(false)
    return of(authorities.includes(privilege))
  }

  logout(redirectPathOrUrl: string = window.location.origin): void {
    const keycloak = this.keycloak;
    this.resetSession();

    if (keycloak) {
      keycloak.logout({redirectUri: this.toAbsoluteUrl(redirectPathOrUrl)});
    }
  }

  clearSession(): void {
    this.resetSession();
  }

  private resetSession(): void {
    LocalStorageService.clear();
    this.keycloak = null;
    this.initPromise = null;
  }

  getAuth(): IAuth {
    if (this.keycloak?.authenticated) {
      return this.buildAuthSnapshot();
    }
    throw new Error('Auth payload is not available');
  }

  getFirstName(): string {
    if (!this.isAuthenticated()) return ''
    return this.getPayload().family_name
  }

  getAccessToken(): string {
    if (this.keycloak?.token) {
      return this.keycloak.token;
    }
    throw new Error('Access token is not available');
  }

  getIdToken(): string | null {
    return this.keycloak?.idToken ?? null;
  }

  getRefreshToken(): string {
    if (this.keycloak?.refreshToken) {
      return this.keycloak.refreshToken;
    }
    throw new Error('Refresh token is not available');
  }


  getOrganizationsFromToken(): string[] {
    if (!this.isAuthenticated()) return [];
    const orgs = this.getPayload()?.organization;
    if (!orgs) return [];
    if (Array.isArray(orgs)) return orgs;
    if (typeof orgs === 'object') return Object.keys(orgs);
    return [];
  }

  private resolveKeycloakConfig(): { url: string; realm: string; clientId: string } {
    const realmMarker = '/realms/';
    const markerIndex = environment.realm_url.indexOf(realmMarker);
    if (markerIndex < 0) {
      throw new Error(`Invalid realm URL format: ${environment.realm_url}`);
    }

    const url = environment.realm_url.substring(0, markerIndex);
    const realm = environment.realm_url.substring(markerIndex + realmMarker.length);
    if (!url || !realm) {
      throw new Error(`Unable to parse Keycloak config from realm URL: ${environment.realm_url}`);
    }

    return {
      url,
      realm,
      clientId: environment.auth_client_id
    };
  }

  private buildAuthSnapshot(): IAuth {
    const keycloak = this.requireKeycloak();
    const now = Math.floor(Date.now() / 1000);
    const tokenExp = keycloak.tokenParsed?.exp ?? now;
    const refreshExp = keycloak.refreshTokenParsed?.exp ?? now;
    const sessionState = keycloak.tokenParsed?.session_state ?? keycloak.tokenParsed?.['sid'] ?? '';

    return {
      access_token: keycloak.token ?? '',
      expires_in: Math.max(0, tokenExp - now),
      refresh_expires_in: Math.max(0, refreshExp - now),
      refresh_token: keycloak.refreshToken ?? '',
      token_type: 'Bearer',
      not_before_policy: 0,
      session_state: sessionState,
      scope: keycloak.tokenParsed?.['scope'] ?? null,
      id_token: keycloak.idToken ?? null,
    };
  }

  private requireKeycloak(): Keycloak {
    if (!this.keycloak) {
      throw new Error('Keycloak has not been initialized');
    }
    return this.keycloak;
  }

  private toAbsoluteUrl(pathOrUrl: string): string {
    if (!pathOrUrl) {
      return window.location.href;
    }

    if (pathOrUrl.startsWith('http://') || pathOrUrl.startsWith('https://')) {
      return pathOrUrl;
    }

    if (!pathOrUrl.startsWith('/')) {
      return window.location.origin + '/' + pathOrUrl;
    }

    return window.location.origin + pathOrUrl;
  }

  private getLoginScopes(): string {
    const configuredScopes = (environment.auth_scopes || '').trim();
    const scopeSet = new Set(
      configuredScopes
        .split(/\s+/)
        .map(scope => scope.trim())
        .filter(scope => scope.length > 0)
    );

    scopeSet.add('openid');
    scopeSet.add('organization');

    return Array.from(scopeSet).join(' ');
  }

  private shouldInitializeOnStartup(locationRef: Location): boolean {
    const path = this.resolveEffectivePath(locationRef);
    const searchParams = new URLSearchParams(locationRef.search || '');

    // Process OIDC callback responses wherever they land.
    const callbackKeys = ['code', 'state', 'session_state', 'iss', 'error'];
    const hasCallbackParams = callbackKeys.some(key => searchParams.has(key));
    if (hasCallbackParams) {
      return true;
    }

    // Public routes should not trigger startup token/session checks.
    const protectedPrefixes = ['/dash', '/cms'];
    return protectedPrefixes.some(prefix => path === prefix || path.startsWith(`${prefix}/`));
  }

  private resolveEffectivePath(locationRef: Location): string {
    const pathname = (locationRef.pathname || '/').trim() || '/';
    const hash = locationRef.hash || '';

    if (hash.startsWith('#/')) {
      return hash.slice(1).split('?')[0] || '/';
    }

    // Strip the locale base href prefix (e.g. '/en') so protected route
    // detection works regardless of the i18n deployment path.
    const baseHref = (document.querySelector('base')?.getAttribute('href') || '/').replace(/\/$/, '');
    if (baseHref && baseHref !== '/' && pathname.startsWith(baseHref)) {
      return pathname.substring(baseHref.length) || '/';
    }

    return pathname;
  }

}
